import type { FC, ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Link } from "@mui/material";

import clsx from "clsx";

import { DateTime } from "luxon";

import { Scrollbars, type positionValues } from "react-custom-scrollbars-2";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";

import ClockIcon from "assets/icons/outlined/clock.svg?react";
import LocationIcon from "assets/icons/outlined/location.svg?react";

import { getPaths } from "core/getPaths";

import type { MatchDay } from "modules/Competition/View/Components/ScheduleGroupModal/editSchedule";

import { CardWrapper, Chip } from "shared/Components";
import { HeaderLine } from "shared/Components/Table/style";
import type { AdvancingTeamRuleModel, FullTeamModel, MatchModel } from "shared/types";
import { ImageSizesEnum, Img, SkeletonBase, Text } from "shared/uikit";
import { TextStylesEnum, TextVariantsEnum } from "shared/uikit/types";

import { getDateTime } from "utils/getDateTime";
import { getMatchGroupRoundName, getTeamNameByAdvancedRule } from "utils/getMatchInfo";
import { getMatchTime } from "utils/matchTime";

import { ChipStyles } from "../../Components/Chip";

const { match: matchRoutes } = getPaths();

const loaderOffset = 180; // loader height (172px) + padding (8px)

interface MatchesListingTemplateProps {
	matchListings: MatchModel[];
	loading?: boolean;
	onLoadMore?: () => void;
	onLoadBefore?: () => void;
	allowReverseScroll?: boolean;
	isTournamentMatches?: boolean;
	dataExportBtn?: null | ReactNode;
}

const MatchesListingTemplate: FC<MatchesListingTemplateProps> = ({
	matchListings,
	loading,
	onLoadMore,
	onLoadBefore,
	allowReverseScroll,
	isTournamentMatches,
	dataExportBtn
}) => {
	const { t } = useTranslation();

	const [matches, setMatches] = useState<MatchDay[]>([]);

	const scrollerRef = useRef<(Scrollbars & { view: HTMLElement }) | null>(null);
	const lastScrollElInfo = useRef({
		id: "",
		offset: 0
	});

	const [scrollingDown, setScrollingDown] = useState(false);

	useEffect(() => {
		const currentScroller = scrollerRef.current;
		const handleFrameScroll = (event: WheelEvent) => {
			const scrollTop = currentScroller?.getScrollTop() || 0;
			if (scrollTop < loaderOffset && event.deltaY < 0) {
				const koeff = event.deltaY <= -10 ? 0.01 : 0.65; // 65% of default speed scroll
				// reduce scroll speed to be sure user literally want to load previous events
				currentScroller?.scrollTop(scrollTop - event.deltaY * koeff);
			}
		};

		if (allowReverseScroll) {
			currentScroller?.view?.addEventListener("wheel", handleFrameScroll, { passive: false });
		}

		return () => {
			currentScroller?.view?.removeEventListener("wheel", handleFrameScroll);
		};
	}, [allowReverseScroll]);

	useEffect(() => {
		if (!loading && !!matches.length && allowReverseScroll) {
			if (scrollerRef.current && !scrollingDown) {
				const el = document.getElementById(lastScrollElInfo.current.id);
				if (el) {
					scrollerRef.current.scrollTop(el.offsetTop - lastScrollElInfo.current.offset);
				} else {
					scrollerRef.current.scrollTop(loaderOffset);
				}
			}
		}
	}, [matches.length, loading, scrollingDown, allowReverseScroll]);

	const handleScroll = useCallback(
		(values: positionValues) => {
			if (values.top >= 0.99 && !loading) {
				setScrollingDown(true);
				onLoadMore?.();
			}
		},
		[loading, onLoadMore]
	);

	const handleStopScroll = useCallback(() => {
		if (scrollerRef.current && allowReverseScroll) {
			const scrolledTop = scrollerRef.current.getScrollTop();
			if (!scrolledTop) {
				if (!loading && matches.length) {
					const id = matches[0]!.matches[0]?.id;

					const elId = `event-id-${id}`;
					const el = document.getElementById(elId);
					if (el) {
						lastScrollElInfo.current = {
							id: elId,
							offset: el.offsetTop
						};
					}

					setScrollingDown(false);
					onLoadBefore?.();
				}
			} else if (scrolledTop < loaderOffset) {
				scrollerRef.current.view?.scroll({
					top: loaderOffset,
					behavior: "smooth"
				});
			}
		}
	}, [onLoadBefore, loading, allowReverseScroll, matches]);

	useEffect(() => {
		let newDateList: MatchDay[] = [];

		if (matchListings.length) {
			const matchListingsWithLocalDates = matchListings.map(x => {
				let date = x.date;
				if (x.startsAt) {
					const dateTime = getDateTime(x.startsAt);
					if (!dateTime.invalidReason) {
						date = dateTime.toISODate()!;
					}
				}
				return {
					...x,
					date
				};
			});

			const dates = [...new Set(matchListingsWithLocalDates.map(item => item.date))];

			newDateList = dates.map(date => {
				const matches = matchListingsWithLocalDates.filter(item => item.date === date) || [];
				return { date, matches } as MatchDay;
			});
		}

		setMatches(newDateList);
	}, [matchListings]);

	const renderTeam = useCallback(
		({
			isHome,
			team,
			advancingRule,
			match,
			matchGroupName
		}: {
			isHome: boolean;
			team?: FullTeamModel | null;
			advancingRule?: AdvancingTeamRuleModel | null;
			match?: Partial<MatchModel> | null;
			matchGroupName?: string;
		}) => {
			if (team) {
				return (
					<div className={clsx("w-1/2 min-w-0 flex flex-col", isHome && "items-end text-right")}>
						<Text variants={TextVariantsEnum.BodyMedium} className="w-full truncate">
							{team?.name}
						</Text>
						<div className="flex items-center gap-2 mt-1">
							<Img size={ImageSizesEnum.Small24} src={team?.club?.logo} alt={team?.club?.name} circle />
							<Text variants={TextVariantsEnum.Caption3} className="text-hint truncate">
								{team?.club?.name}
							</Text>
						</div>
					</div>
				);
			}

			if (advancingRule) {
				const teamName = getTeamNameByAdvancedRule({
					isHome,
					advancingRule,
					match,
					matchGroupName,
					t
				});

				return (
					<div className={clsx("w-1/2 min-w-0 flex flex-col", isHome && "items-end text-right")}>
						<Text variants={TextVariantsEnum.BodyMedium} className="w-full truncate">
							{teamName}
						</Text>
						<div className="flex items-center gap-2 mt-1">
							<Img size={ImageSizesEnum.Small24} src={null} alt={"?"} circle />
						</div>
					</div>
				);
			}

			return null;
		},
		[t]
	);

	const renderMobileTeam = useCallback(
		({
			isHome,
			team,
			advancingRule,
			match,
			matchGroupName
		}: {
			isHome: boolean;
			team?: FullTeamModel | null;
			advancingRule?: AdvancingTeamRuleModel | null;
			match?: Partial<MatchModel> | null;
			matchGroupName?: string;
		}) => {
			const teamName = team
				? team.name
				: advancingRule
					? getTeamNameByAdvancedRule({
							isHome,
							advancingRule,
							match,
							matchGroupName,
							t
						})
					: "-";
			const teamLogo = team?.club?.logo || null;
			const clubName = team?.club?.name || "";
			const score =
				match?.result && typeof (isHome ? match?.homeTeam?.score : match?.awayTeam?.score) === "number"
					? isHome
						? match?.homeTeam?.score
						: match?.awayTeam?.score
					: undefined;

			return (
				<div className="flex items-center justify-between gap-3 rounded-lg bg-gray-50 px-3 py-2">
					<div className="flex items-center gap-2 min-w-0">
						<Img size={ImageSizesEnum.Small32} src={teamLogo} alt={teamName} circle className="shrink-0" />
						<div className="min-w-0">
							<Text variants={TextVariantsEnum.BodyMedium} className="truncate">
								{teamName}
							</Text>
							<Text variants={TextVariantsEnum.Caption3} className="text-hint truncate">
								{clubName || "-"}
							</Text>
						</div>
					</div>
					<Text variants={TextVariantsEnum.H7} className="shrink-0">
						{typeof score === "number" ? score : "-"}
					</Text>
				</div>
			);
		},
		[t]
	);

	const renderMatch = useCallback(
		(match: MatchModel, addBorder: boolean) => {
			const facilityName = match?.field?.facility?.name || match?.facility?.name || "";
			const fieldName = match?.field?.name;
			const location =
				facilityName || fieldName ? `${facilityName ? facilityName : ""} ${fieldName ? `/ ${fieldName}` : ""}` : "-";

			return (
				<Link
					key={match.id}
					to={`${matchRoutes.match.getPath()}/${match.id}`}
					component={RouterLink}
					className={clsx("no-underline p-4 max-sm:block", addBorder && "border-t border-gray-100")}
				>
					<div className="max-sm:hidden">
						{isTournamentMatches && (
							<div className="flex items-center justify-between mb-4">
								<div className="w-4/12 flex">
									<Chip text={getMatchGroupRoundName(match, t)} chipStyle={ChipStyles.LIGHT_FILL} />
									{(match?.competitionOrd || 0) > 0 && (
										<Chip text={`#${match.competitionOrd}`} chipStyle={ChipStyles.LIGHT_FILL} className="ml-2" />
									)}
								</div>
								<div className="w-4/12 flex justify-center">
									{match?.group?.competition?.name && (
										<Chip text={match.group.competition.name} chipStyle={ChipStyles.LIGHT_FILL} />
									)}
								</div>
								<div className="w-4/12 flex justify-end">
									{match?.division?.name && <Chip text={match.division.name} chipStyle={ChipStyles.LIGHT_FILL} />}
								</div>
							</div>
						)}
						{(match?.homeTeam || match?.awayTeam || match?.homeTeamAdvancingRule || match?.awayTeamAdvancingRule) && (
							<div className="flex align-center justify-center gap-7">
								{renderTeam({
									team: match?.homeTeam,
									isHome: true,
									advancingRule: match?.homeTeamAdvancingRule,
									match,
									matchGroupName: match?.group?.name
								})}
								<Text variants={TextVariantsEnum.BodyMedium} className="shrink-0">
									{match.result &&
									((match?.homeTeam && match?.homeTeam?.score !== null) ||
										(match?.awayTeam && match?.awayTeam?.score !== null))
										? `${match?.homeTeam?.score || "0"} - ${match?.awayTeam?.score || "0"}`
										: "-"}
								</Text>
								{renderTeam({
									team: match?.awayTeam,
									isHome: false,
									advancingRule: match?.awayTeamAdvancingRule,
									match,
									matchGroupName: match?.group?.name
								})}
							</div>
						)}
						<div className="flex items-center justify-between">
							<div className="flex items-center gap-1 w-1/2">
								<ClockIcon className="w-5 h-5 svg-paths:stroke-gray-400" />
								<Text variants={TextVariantsEnum.BodyMedium} className="text-hint">
									{getMatchTime(match?.playedAt || null, match?.endsAt || null)}
								</Text>
							</div>
							<div className="flex items-center gap-1 w-1/2">
								<Text variants={TextVariantsEnum.BodyMedium} className="text-hint truncate w-3/4 ml-auto text-right">
									{location}
								</Text>
								<LocationIcon className="w-5 h-5 svg-paths:stroke-gray-400" />
							</div>
						</div>
					</div>
					<div className="hidden max-sm:flex max-sm:flex-col max-sm:gap-3">
						{isTournamentMatches && (
							<div className="flex flex-wrap gap-2">
								<Chip
									text={getMatchGroupRoundName(match, t)}
									chipStyle={ChipStyles.LIGHT_FILL}
									className="max-w-full [&_*]:truncate"
								/>
								{(match?.competitionOrd || 0) > 0 && (
									<Chip text={`#${match.competitionOrd}`} chipStyle={ChipStyles.LIGHT_FILL} />
								)}
								{match?.group?.competition?.name && (
									<Chip
										text={match.group.competition.name}
										chipStyle={ChipStyles.LIGHT_FILL}
										className="max-w-full [&_*]:truncate"
									/>
								)}
								{match?.division?.name && (
									<Chip
										text={match.division.name}
										chipStyle={ChipStyles.LIGHT_FILL}
										className="max-w-full [&_*]:truncate"
									/>
								)}
							</div>
						)}
						<div className="flex flex-col gap-2">
							{renderMobileTeam({
								team: match?.homeTeam,
								isHome: true,
								advancingRule: match?.homeTeamAdvancingRule,
								match,
								matchGroupName: match?.group?.name
							})}
							{renderMobileTeam({
								team: match?.awayTeam,
								isHome: false,
								advancingRule: match?.awayTeamAdvancingRule,
								match,
								matchGroupName: match?.group?.name
							})}
						</div>
						<div className="rounded-lg bg-gray-50 px-3 py-2 flex flex-col gap-2">
							<div className="flex items-center gap-2">
								<ClockIcon className="w-5 h-5 shrink-0 svg-paths:stroke-gray-400" />
								<Text variants={TextVariantsEnum.BodyMedium} className="text-hint truncate">
									{getMatchTime(match?.playedAt || null, match?.endsAt || null)}
								</Text>
							</div>
							<div className="flex items-center gap-2">
								<LocationIcon className="w-5 h-5 shrink-0 svg-paths:stroke-gray-400" />
								<Text variants={TextVariantsEnum.BodyMedium} className="text-hint truncate">
									{location}
								</Text>
							</div>
						</div>
					</div>
				</Link>
			);
		},
		[renderTeam, renderMobileTeam, isTournamentMatches, t]
	);

	const renderMatchDay = useCallback(
		(matchDay: MatchDay) => (
			<div className="flex flex-col" key={matchDay?.date ? matchDay.date : "unscheduled matches date"}>
				<div className="px-4">
					<div className="rounded-lg py-3 px-4 bg-gray-50 border border-gray-100">
						<Text variants={TextVariantsEnum.Caption3}>
							{matchDay.date
								? DateTime.fromISO(matchDay.date)?.toFormat("cccc, d LLLL")
								: t("match:unscheduled_matches")}
						</Text>
					</div>
				</div>
				{matchDay.matches.map((match, i) => renderMatch(match, i > 0))}
			</div>
		),
		[renderMatch, t]
	);

	const renderTeamSkeleton = useCallback(
		(home?: boolean) => (
			<div className={clsx("w-1/2 flex flex-col", home && "items-end")}>
				<SkeletonBase width={130} height={24} fullScale />
				<div className="flex items-center gap-2 mt-1">
					<SkeletonBase width={24} height={24} variant="circular" />
					<SkeletonBase width={60} height={16} fullScale />
				</div>
			</div>
		),
		[]
	);

	const matchSkeleton = useMemo(
		() => (
			<div className="flex flex-col mb-4">
				<div className="rounded-lg py-3 px-4 bg-gray-50 border border-gray-100 mx-4">
					<SkeletonBase width={150} height={16} fullScale />
				</div>
				<div className="pt-4 border-t border-gray-100 mt-4">
					<div className="flex align-center justify-center gap-7">
						{renderTeamSkeleton(true)}
						<div className="self-start flex items-center gap-1">
							<SkeletonBase width={40} height={16} fullScale />
						</div>
						{renderTeamSkeleton(false)}
					</div>
					<div className="flex items-center justify-between px-4">
						<div className="flex items-center gap-1">
							<SkeletonBase width={20} height={20} fullScale />
							<SkeletonBase width={140} height={24} fullScale />
						</div>
						<div className="flex items-center gap-1">
							<SkeletonBase width={180} height={24} fullScale />
							<SkeletonBase width={20} height={20} fullScale />
						</div>
					</div>
				</div>
			</div>
		),
		[renderTeamSkeleton]
	);

	return (
		<CardWrapper noPadding className="p-0 flex-1 overflow-hidden flex flex-col">
			<HeaderLine className="flex items-center justify-between p-4">
				{loading ? (
					<SkeletonBase width={120} height={33} fullScale />
				) : (
					<>
						<Text style={TextStylesEnum.Default} variants={TextVariantsEnum.H6}>
							{t("matches")}
						</Text>
						{dataExportBtn}
					</>
				)}
				{loading && <SkeletonBase width={150} height={40} fullScale />}
			</HeaderLine>
			<div className="flex items-center justify-between px-4 my-4 max-sm:hidden">
				{loading ? (
					<>
						<SkeletonBase width={60} height={19} fullScale />
						<div className="flex items-center gap-6">
							<SkeletonBase width={60} height={19} fullScale />
							<SkeletonBase width={40} height={19} fullScale />
							<SkeletonBase width={60} height={19} fullScale />
						</div>
						<SkeletonBase width={60} height={19} fullScale />
					</>
				) : (
					<div className="flex justify-between items-center w-full relative">
						<Text variants={TextVariantsEnum.H7} className="styles_hint">
							{t("date")}
						</Text>
						<div className="absolute top-0 left-0 w-full flex items-center justify-center gap-6">
							<Text variants={TextVariantsEnum.H7} className="styles_hint">
								{t("match:home_team")}
							</Text>
							<Text variants={TextVariantsEnum.H7} className="styles_hint">
								{t("match:score")}
							</Text>
							<Text variants={TextVariantsEnum.H7} className="styles_hint">
								{t("match:away_team")}
							</Text>
						</div>
						<Text variants={TextVariantsEnum.H7} className="styles_hint">
							{t("location")}
						</Text>
					</div>
				)}
			</div>
			<div className="flex flex-col flex-1">
				<div className="flex-grow">
					<Scrollbars
						ref={scrollerRef}
						className="w-full flex flex-col gap-4"
						height="100%"
						autoHide
						onUpdate={handleScroll}
						onScrollStop={handleStopScroll}
						renderView={props => <div {...props} className="flex flex-col gap-4 mb-0" />}
					>
						{allowReverseScroll && <div>{matchSkeleton}</div>}
						{!!matches?.length && matches.map(matchDay => renderMatchDay(matchDay))}
						{loading && (
							<>
								{matchSkeleton}
								{matchSkeleton}
							</>
						)}
					</Scrollbars>
				</div>
			</div>
		</CardWrapper>
	);
};

export default MatchesListingTemplate;
